import { all, fork, put, takeEvery, call } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';

// apicore
import { APICore, setAuthorization } from 'src/helpers/api/apiCore';

// api
import {
    getDashboardMain as getDashboardMainApi,
    getReportReady as getReportReadyApi,
    getPatientList as getPatientListApi,
    getCategorySales as getCategorySalesApi,
} from './api';

// actions
import { dashboardApiResponseSuccess, dashboardApiResponseError } from './actions';

// constants
import { DashboardActionTypes } from './constants';

type DashboardData = {
    payload: any;
    type: string;
};

const api = new APICore();

/**
 * Fetch Dashboard Main
 * @param {*} payload
 */
function* GetDashboardMain({ payload, type }: DashboardData): SagaIterator {
    try {
        const response = yield call(getDashboardMainApi, payload);
        const { data, message, success, code } = response.data;
        if (code === 200 && success) {
            yield put(
                dashboardApiResponseSuccess(DashboardActionTypes.GET_DASHBOARD_MAIN, data, message, code, false, false)
            );
        } else {
            let msg = `An error while getting dashboard main info!`;
            if (message) msg = message;
            yield put(
                dashboardApiResponseSuccess(DashboardActionTypes.GET_DASHBOARD_MAIN, data, msg, code, true, false)
            );
        }
    } catch (error: any) {
        yield put(dashboardApiResponseError(DashboardActionTypes.GET_DASHBOARD_MAIN, [], error, 404, true, false));
    }
}

/**
 * Fetch Category sales
 * @param {*} payload
 */
function* GetCategorySales({ payload, type }: DashboardData): SagaIterator {
    try {
        const response = yield call(getCategorySalesApi, payload);
        const { data, message, success, code } = response.data;
        if (code === 200 && success) {
            yield put(
                dashboardApiResponseSuccess(DashboardActionTypes.GET_CATEGORY_SALES, data, message, code, false, false)
            );
        } else {
            let msg = `An error while getting category sales info!`;
            if (message) msg = message;
            yield put(
                dashboardApiResponseSuccess(DashboardActionTypes.GET_CATEGORY_SALES, data, msg, code, true, false)
            );
        }
    } catch (error: any) {
        yield put(dashboardApiResponseError(DashboardActionTypes.GET_CATEGORY_SALES, [], error, 404, true, false));
    }
}

/**
 * Fetch Category yearly sales
 * @param {*} payload
 */
function* GetCategoryYearlySales({ payload, type }: DashboardData): SagaIterator {
    try {
        const response = yield call(getCategorySalesApi, payload);
        const { data, message, success, code } = response.data;
        if (code === 200 && success) {
            yield put(
                dashboardApiResponseSuccess(
                    DashboardActionTypes.GET_CATEGORY_YEARLY_SALES,
                    data,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting category yearly sales info!`;
            if (message) msg = message;
            yield put(
                dashboardApiResponseSuccess(
                    DashboardActionTypes.GET_CATEGORY_YEARLY_SALES,
                    data,
                    msg,
                    code,
                    true,
                    false
                )
            );
        }
    } catch (error: any) {
        yield put(
            dashboardApiResponseError(DashboardActionTypes.GET_CATEGORY_YEARLY_SALES, [], error, 404, true, false)
        );
    }
}

/**
 * Fetch Report Ready
 * @param {*} payload
 */
function* GetReportReady({ payload, type }: DashboardData): SagaIterator {
    try {
        const response = yield call(getReportReadyApi, payload);
        const { data, message, success, code } = response.data;

        const responseData = typeof data === 'object' && Object.keys(data).length === 0 ? [] : data;
        if (code === 200 && success) {
            yield put(
                dashboardApiResponseSuccess(
                    DashboardActionTypes.GET_REPORT_READY,
                    responseData,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting report ready Listing!`;
            if (message) msg = message;
            yield put(
                dashboardApiResponseSuccess(DashboardActionTypes.GET_REPORT_READY, responseData, msg, code, true, false)
            );
        }
    } catch (error: any) {
        yield put(dashboardApiResponseError(DashboardActionTypes.GET_REPORT_READY, [], error, 404, true, false));
    }
}

/**
 * Fetch Patient List
 * @param {*} payload
 */
function* GetPatientList({ payload, type }: DashboardData): SagaIterator {
    try {
        const response = yield call(getPatientListApi, payload);
        const { data, message, success, code } = response.data;

        const responseData = typeof data === 'object' && Object.keys(data).length === 0 ? [] : data;
        if (code === 200 && success) {
            yield put(
                dashboardApiResponseSuccess(
                    DashboardActionTypes.GET_PATIENT_LIST,
                    responseData,
                    message,
                    code,
                    false,
                    false
                )
            );
        } else {
            let msg = `An error while getting patient Listing!`;
            if (message) msg = message;
            yield put(
                dashboardApiResponseSuccess(DashboardActionTypes.GET_PATIENT_LIST, responseData, msg, code, true, false)
            );
        }
    } catch (error: any) {
        yield put(dashboardApiResponseError(DashboardActionTypes.GET_PATIENT_LIST, [], error, 404, true, false));
    }
}

export function* watchGetDashboardMain() {
    yield takeEvery(DashboardActionTypes.GET_DASHBOARD_MAIN, GetDashboardMain);
}

export function* watchGetReportReady() {
    yield takeEvery(DashboardActionTypes.GET_REPORT_READY, GetReportReady);
}

export function* watchGetPatientList() {
    yield takeEvery(DashboardActionTypes.GET_PATIENT_LIST, GetPatientList);
}
export function* watchGetCategorySales() {
    yield takeEvery(DashboardActionTypes.GET_CATEGORY_SALES, GetCategorySales);
}

export function* watchGetCategoryYearlySales() {
    yield takeEvery(DashboardActionTypes.GET_CATEGORY_YEARLY_SALES, GetCategoryYearlySales);
}

function* dashboardSaga() {
    yield all([
        fork(watchGetDashboardMain),
        fork(watchGetReportReady),
        fork(watchGetPatientList),
        fork(watchGetCategorySales),
        fork(watchGetCategoryYearlySales),
    ]);
}

export default dashboardSaga;
